// Traductions du mode Lite : page « stats ».
// Constats (insights) : `[[x]]` = chiffre en gras (police mono), `**x**` = texte en gras.
export const fr = {
  title: "Stats",
  subtitle: "Toutes tes analytics, rangées par question.",
  tabs: {
    overview: { label: "Vue d'ensemble" },
    timing: { label: "Timing" },
    instruments: { label: "Instruments et setups" },
    risk: { label: "Risque" },
    psycho: { label: "Psychologie" },
  },
  empty: {
    title: "Tes stats arrivent avec tes trades",
    text: "Quand tu gagnes, sur quoi, avec quel risque, et ce que tes émotions te coûtent. Une vingtaine de trades suffit pour les premiers constats.",
  },
  infinite: "∞",
  none: "-",
  buckets: {
    hold: { lt2: "< 2 min", "2to5": "2 à 5 min", "5to15": "5 à 15 min", "15to30": "15 à 30 min", gt30: "> 30 min" },
    size: { "1": "1 contrat", "2": "2 contrats", "3": "3 contrats", "4plus": "4 et plus" },
    slot: {
      open: "Ouverture (15 h 30 à 16 h)",
      morning: "Matinée US (16 h à 17 h 30)",
      lunch: "Mi-journée (17 h 30 à 19 h)",
      pm: "Après-midi (19 h à 22 h)",
      out: "Hors séance",
    },
    rank: { "1": "1er trade", "2": "2e trade", "3": "3e trade", "4to5": "4e et 5e", "6plus": "6e et au-delà" },
    mood: { excellent: "Excellente", good: "Bonne", neutral: "Neutre", poor: "Moyenne", bad: "Mauvaise" },
  },
  overview: {
    insightPositive: (net: string, days: number, avg: string) => `Tu es positif de [[${net}]] sur ${days} jour${days > 1 ? "s" : ""}, soit [[${avg}]] par jour en moyenne.`,
    insightNegative: (net: string, days: number) => `Tu es négatif de [[${net}]] sur ${days} jour${days > 1 ? "s" : ""}.`,
    insightFees: (ratio: string, fees: string) => `Les frais représentent [[${ratio}]] de ton brut ([[${fees}]]).`,
    insightFeesHigh: " Moins de trades ou des micros réduiraient la facture.",
    insightWinLoss: (win: string, loss: string, higher: boolean) =>
      `Ton gain moyen (${win}) est ${higher ? "supérieur" : "inférieur"} à ta perte moyenne (${loss}).`,
    insightBreakeven: (rate: string) => ` Il te faut plus de ${rate} de gagnants pour être rentable.`,
    keyFigures: "Chiffres clés",
    m: {
      net: "Résultat net",
      gross: "Résultat brut",
      grossHint: "Avant frais",
      fees: "Frais",
      feesHint: (v: string) => `${v} par trade`,
      winRate: "Trades gagnants",
      winRateHint: (w: number, l: number) => `${w} G · ${l} P`,
      pf: "Profit factor",
      pfHint: "Gains bruts / pertes brutes",
      expectancy: "Espérance par trade",
      avgWin: "Gain moyen",
      avgLoss: "Perte moyenne",
      ratio: "Ratio gain / perte",
      best: "Plus gros gain",
      worst: "Plus grosse perte",
      avgDaily: "Résultat moyen par jour",
      winDays: "Jours gagnants",
      winDaysHint: (w: number, n: number) => `${w} sur ${n}`,
      bestDay: "Meilleur jour",
      worstDay: "Pire jour",
      perDay: "Trades par jour",
      avgHold: "Durée moyenne",
      streaks: "Séries max",
      streaksHint: "Gagnants / perdants d'affilée",
    },
    equityTitle: "Courbe de résultat",
    dailyTitle: "Résultat par jour",
    cumulative: "Cumul",
    perDay: "Par jour",
    rollingTitle: "Taux de réussite glissant",
    rollingSubtitle: "Sur les 20 derniers trades : ta forme du moment",
    notEnough: "Pas assez de trades",
    monthsTitle: "Résultat par mois",
    monthsDetail: "Détail par mois",
  },
  timing: {
    insightBestSlot: (slot: string, net: string, rate: string) => `Ton meilleur créneau : **${slot.toLowerCase()}**, avec [[${net}]] et ${rate} de gagnants.`,
    insightWorstSlot: (slot: string, cost: string) => `Le créneau **${slot.toLowerCase()}** te coûte [[${cost}]]. Envisage de ne plus trader à ce moment.`,
    insightWorstDay: (day: string, net: string) => `Le **${day.toLowerCase()}** est ton jour le plus faible ([[${net}]]).`,
    insightBestHold: (hold: string, exp: string) => `Tes trades de **${hold}** ont la meilleure espérance ([[${exp}]] par trade).`,
    byWeekday: "Par jour de la semaine",
    byHour: "Par heure d'entrée",
    heatmap: "Carte des horaires",
    heatmapSubtitle: "Résultat par jour et par heure d'entrée",
    bySlot: "Par créneau de la séance US",
    bySlotSubtitle: "Heure locale de ton navigateur",
    byHold: "Par durée de détention",
    byWeekdayDetail: "Par jour de la semaine (détail)",
  },
  instruments: {
    insightBest: (sym: string, net: string, n: number) => `**${sym}** est ton meilleur instrument : [[${net}]] sur ${n} trade${n > 1 ? "s" : ""}.`,
    insightWorst: (sym: string, loss: string, rate: string) => `**${sym}** te fait perdre [[${loss}]] (${rate} de gagnants). À réduire ou à arrêter.`,
    insightBestStrat: (name: string, pf: string) => `Ta stratégie la plus rentable : **${name}**, profit factor [[${pf}]].`,
    insightSides: (longsBetter: boolean, best: string, worst: string) => `Tes **${longsBetter ? "longs" : "shorts"}** rapportent plus (${best} contre ${worst}).`,
    bySymbol: "Par instrument",
    byStrategy: "Par stratégie",
    byStrategySubtitle: "Un trade lié à plusieurs stratégies compte dans chacune",
    noStrategy: "Sans stratégie",
    sides: "Long ou short",
    sideMeta: (n: number, pf: string) => `${n} trade${n > 1 ? "s" : ""} · PF ${pf}`,
    sideWinners: (rate: string) => `${rate} gagnants`,
    sidesBySymbol: "Long ou short, par instrument",
    longWinners: "Long (gagnants)",
    shortWinners: "Short (gagnants)",
    long: "Long",
    short: "Short",
    rateAndCount: (rate: string, n: number) => `${rate} · ${n}`,
    byAccount: "Par compte",
    noAccount: "Sans compte",
    unknownAccount: "Compte supprimé",
  },
  risk: {
    insightConsistency: (share: string) => `Ton meilleur jour pèse [[${share}]] de ton profit total.`,
    insightConsistencyBad: " La plupart des prop firms exigent moins de 30 à 50 % : attention avant de demander un payout.",
    insightConsistencyGood: " Ta régularité respecte les règles de constance des prop firms.",
    insightVar: (varLoss: string, dd: string, days: string) => `Un jour sur 20, tu perds plus de [[${varLoss}]]. Ton drawdown max ([[${dd}]]) vaut ${days} jours moyens.`,
    insightSize: (size: string, exp: string, avg: string) => `Avec **${size}**, ton espérance est de [[${exp}]] par trade, contre ${avg} en moyenne.`,
    insightSizeBad: " Tu performes moins bien quand tu augmentes la taille.",
    insightSizeGood: " Tu tiens bien la taille.",
    indicators: "Indicateurs de risque",
    m: {
      maxDd: "Drawdown max",
      maxDdHint: "Du plus haut au plus bas",
      recovery: "Recovery factor",
      recoveryHint: "Résultat / drawdown max",
      sharpe: "Ratio de Sharpe",
      sharpeHint: "Annualisé, sur les jours",
      sortino: "Ratio de Sortino",
      sortinoHint: "Ne pénalise que la baisse",
      consistency: "Constance",
      consistencyHint: "Part du meilleur jour",
      var: "VaR 95 % journalière",
      varHint: "Perte dépassée 1 jour sur 20",
      std: "Écart-type journalier",
      stdHint: "Volatilité de tes journées",
      worstDay: "Pire jour",
      worst: "Plus grosse perte",
      maxLossStreak: "Pertes d'affilée max",
      lossDays: "Jours perdants",
      lossDaysHint: (n: number) => `sur ${n}`,
      avgLoss: "Perte moyenne",
    },
    drawdown: "Drawdown",
    drawdownSubtitle: "Distance à ton plus haut, jour après jour",
    distribution: "Distribution des journées",
    distributionSubtitle: "Nombre de jours par tranche de résultat",
    below: (v: string) => `< ${v}`,
    above: (v: string) => `> ${v}`,
    scatter: "Chaque trade",
    scatterSubtitle: "Les points isolés en bas sont tes pertes hors norme",
    bySize: "Par taille de position",
  },
  psycho: {
    insightRevenge: (rate: string, normal: string, net: string) =>
      `Moins de 10 min après une perte, tu gagnes [[${rate}]] du temps contre ${normal} sinon. Ces trades « revanche » totalisent [[${net}]].`,
    insightAfterGoal: (n: number, net: string) => `Après avoir atteint ton objectif du jour, tu as continué sur ${n} trade${n > 1 ? "s" : ""} pour [[${net}]].`,
    insightAfterGoalBad: " T'arrêter à l'objectif t'aurait fait gagner plus.",
    insightTag: (tag: string, cost: string, n: number) => `Le tag **« ${tag} »** te coûte [[${cost}]] sur ${n} trade${n > 1 ? "s" : ""}.`,
    insightRank: (first: string, late: string, rank: string) => `Ton 1er trade du jour rapporte [[${first}]] en moyenne, contre ${late} pour le ${rank.toLowerCase()}.`,
    revenge: "Trades de revanche",
    revengeQuestion: "Que se passe-t-il quand tu reprends vite après une perte ?",
    revengeA: "< 10 min après une perte",
    revengeB: "Les autres",
    revengeVerdict: (gap: string) => `Écart d'espérance : ${gap} par trade.`,
    twoLosses: "Après 2 pertes d'affilée",
    twoLossesQuestion: "Sais-tu t'arrêter quand ça ne va pas ?",
    twoLossesA: "Après 2 pertes",
    twoLossesB: "Moyenne",
    goal: "Objectif atteint",
    goalSubtitle: "Les trades pris une fois l'objectif du jour atteint",
    goalMeta: (n: number, rate: string) => `${n} trade${n > 1 ? "s" : ""} · ${rate} gagnants`,
    goalNotSet: "Aucun objectif PnL quotidien. Crée-le dans Discipline.",
    goalCurrent: (v: string) => `Objectif actuel : ${v} par jour.`,
    byRank: "Par rang du trade dans la journée",
    byRankSubtitle: "La fatigue et l'excès de confiance se voient ici",
    byMood: "Par ressenti de la journée",
    byMoodSubtitle: "D'après l'humeur notée dans tes sessions de trading",
    moodEmpty: "Aucun ressenti enregistré",
    moodEmptyText: "Note ton humeur en fin de session de trading : on croisera ici ton ressenti avec tes résultats.",
    byTag: "Par tag",
    byTagSubtitle: "Les tags que tu poses sur tes trades",
    tagEmpty: "Aucun tag sur ces trades",
    tagEmptyText: "Ajoute des tags depuis le détail d'un trade pour voir ce qu'ils te rapportent ou te coûtent.",
  },
};

export const en: typeof fr = {
  title: "Stats",
  subtitle: "All your analytics, organized by question.",
  tabs: {
    overview: { label: "Overview" },
    timing: { label: "Timing" },
    instruments: { label: "Instruments and setups" },
    risk: { label: "Risk" },
    psycho: { label: "Psychology" },
  },
  empty: {
    title: "Your stats come with your trades",
    text: "When you win, on what, with how much risk, and what your emotions cost you. About twenty trades are enough for the first insights.",
  },
  infinite: "∞",
  none: "-",
  buckets: {
    hold: { lt2: "< 2 min", "2to5": "2 to 5 min", "5to15": "5 to 15 min", "15to30": "15 to 30 min", gt30: "> 30 min" },
    size: { "1": "1 contract", "2": "2 contracts", "3": "3 contracts", "4plus": "4 and more" },
    slot: {
      open: "Open (3:30 to 4 pm)",
      morning: "US morning (4 to 5:30 pm)",
      lunch: "Midday (5:30 to 7 pm)",
      pm: "Afternoon (7 to 10 pm)",
      out: "Outside session",
    },
    rank: { "1": "1st trade", "2": "2nd trade", "3": "3rd trade", "4to5": "4th and 5th", "6plus": "6th and beyond" },
    mood: { excellent: "Excellent", good: "Good", neutral: "Neutral", poor: "Poor", bad: "Bad" },
  },
  overview: {
    insightPositive: (net: string, days: number, avg: string) => `You are up [[${net}]] over ${days} day${days > 1 ? "s" : ""}, i.e. [[${avg}]] per day on average.`,
    insightNegative: (net: string, days: number) => `You are down [[${net}]] over ${days} day${days > 1 ? "s" : ""}.`,
    insightFees: (ratio: string, fees: string) => `Fees amount to [[${ratio}]] of your gross ([[${fees}]]).`,
    insightFeesHigh: " Fewer trades or micros would cut the bill.",
    insightWinLoss: (win: string, loss: string, higher: boolean) =>
      `Your average win (${win}) is ${higher ? "higher" : "lower"} than your average loss (${loss}).`,
    insightBreakeven: (rate: string) => ` You need more than ${rate} winners to be profitable.`,
    keyFigures: "Key figures",
    m: {
      net: "Net result",
      gross: "Gross result",
      grossHint: "Before fees",
      fees: "Fees",
      feesHint: (v: string) => `${v} per trade`,
      winRate: "Winning trades",
      winRateHint: (w: number, l: number) => `${w} W · ${l} L`,
      pf: "Profit factor",
      pfHint: "Gross wins / gross losses",
      expectancy: "Expectancy per trade",
      avgWin: "Average win",
      avgLoss: "Average loss",
      ratio: "Win / loss ratio",
      best: "Biggest win",
      worst: "Biggest loss",
      avgDaily: "Average result per day",
      winDays: "Winning days",
      winDaysHint: (w: number, n: number) => `${w} of ${n}`,
      bestDay: "Best day",
      worstDay: "Worst day",
      perDay: "Trades per day",
      avgHold: "Average duration",
      streaks: "Max streaks",
      streaksHint: "Wins / losses in a row",
    },
    equityTitle: "Equity curve",
    dailyTitle: "Result per day",
    cumulative: "Cumulative",
    perDay: "Per day",
    rollingTitle: "Rolling win rate",
    rollingSubtitle: "Over the last 20 trades: your current form",
    notEnough: "Not enough trades",
    monthsTitle: "Result per month",
    monthsDetail: "Monthly detail",
  },
  timing: {
    insightBestSlot: (slot: string, net: string, rate: string) => `Your best time slot: **${slot}**, with [[${net}]] and ${rate} winners.`,
    insightWorstSlot: (slot: string, cost: string) => `The **${slot}** slot costs you [[${cost}]]. Consider not trading at that time.`,
    insightWorstDay: (day: string, net: string) => `**${day}** is your weakest day ([[${net}]]).`,
    insightBestHold: (hold: string, exp: string) => `Your **${hold}** trades have the best expectancy ([[${exp}]] per trade).`,
    byWeekday: "By day of the week",
    byHour: "By entry hour",
    heatmap: "Time map",
    heatmapSubtitle: "Result by day and entry hour",
    bySlot: "By US session slot",
    bySlotSubtitle: "Your browser's local time",
    byHold: "By holding time",
    byWeekdayDetail: "By day of the week (detail)",
  },
  instruments: {
    insightBest: (sym: string, net: string, n: number) => `**${sym}** is your best instrument: [[${net}]] over ${n} trade${n > 1 ? "s" : ""}.`,
    insightWorst: (sym: string, loss: string, rate: string) => `**${sym}** loses you [[${loss}]] (${rate} winners). Reduce it or stop.`,
    insightBestStrat: (name: string, pf: string) => `Your most profitable strategy: **${name}**, profit factor [[${pf}]].`,
    insightSides: (longsBetter: boolean, best: string, worst: string) => `Your **${longsBetter ? "longs" : "shorts"}** earn more (${best} vs ${worst}).`,
    bySymbol: "By instrument",
    byStrategy: "By strategy",
    byStrategySubtitle: "A trade linked to several strategies counts in each",
    noStrategy: "No strategy",
    sides: "Long or short",
    sideMeta: (n: number, pf: string) => `${n} trade${n > 1 ? "s" : ""} · PF ${pf}`,
    sideWinners: (rate: string) => `${rate} winners`,
    sidesBySymbol: "Long or short, by instrument",
    longWinners: "Long (winners)",
    shortWinners: "Short (winners)",
    long: "Long",
    short: "Short",
    rateAndCount: (rate: string, n: number) => `${rate} · ${n}`,
    byAccount: "By account",
    noAccount: "No account",
    unknownAccount: "Deleted account",
  },
  risk: {
    insightConsistency: (share: string) => `Your best day weighs [[${share}]] of your total profit.`,
    insightConsistencyBad: " Most prop firms require less than 30 to 50%: be careful before requesting a payout.",
    insightConsistencyGood: " Your consistency meets prop firm consistency rules.",
    insightVar: (varLoss: string, dd: string, days: string) => `One day in 20, you lose more than [[${varLoss}]]. Your max drawdown ([[${dd}]]) equals ${days} average days.`,
    insightSize: (size: string, exp: string, avg: string) => `With **${size}**, your expectancy is [[${exp}]] per trade, vs ${avg} on average.`,
    insightSizeBad: " You perform worse when you size up.",
    insightSizeGood: " You handle size well.",
    indicators: "Risk indicators",
    m: {
      maxDd: "Max drawdown",
      maxDdHint: "From peak to trough",
      recovery: "Recovery factor",
      recoveryHint: "Result / max drawdown",
      sharpe: "Sharpe ratio",
      sharpeHint: "Annualized, on days",
      sortino: "Sortino ratio",
      sortinoHint: "Only penalizes downside",
      consistency: "Consistency",
      consistencyHint: "Share of the best day",
      var: "Daily VaR 95%",
      varHint: "Loss exceeded 1 day in 20",
      std: "Daily standard deviation",
      stdHint: "Volatility of your days",
      worstDay: "Worst day",
      worst: "Biggest loss",
      maxLossStreak: "Max losses in a row",
      lossDays: "Losing days",
      lossDaysHint: (n: number) => `of ${n}`,
      avgLoss: "Average loss",
    },
    drawdown: "Drawdown",
    drawdownSubtitle: "Distance from your peak, day after day",
    distribution: "Daily distribution",
    distributionSubtitle: "Number of days per result range",
    below: (v: string) => `< ${v}`,
    above: (v: string) => `> ${v}`,
    scatter: "Every trade",
    scatterSubtitle: "Isolated dots at the bottom are your outlier losses",
    bySize: "By position size",
  },
  psycho: {
    insightRevenge: (rate: string, normal: string, net: string) =>
      `Less than 10 min after a loss, you win [[${rate}]] of the time vs ${normal} otherwise. These "revenge" trades total [[${net}]].`,
    insightAfterGoal: (n: number, net: string) => `After hitting your daily goal, you kept going for ${n} trade${n > 1 ? "s" : ""} and [[${net}]].`,
    insightAfterGoalBad: " Stopping at your goal would have earned you more.",
    insightTag: (tag: string, cost: string, n: number) => `The **"${tag}"** tag costs you [[${cost}]] over ${n} trade${n > 1 ? "s" : ""}.`,
    insightRank: (first: string, late: string, rank: string) => `Your 1st trade of the day earns [[${first}]] on average, vs ${late} for the ${rank}.`,
    revenge: "Revenge trades",
    revengeQuestion: "What happens when you jump back in right after a loss?",
    revengeA: "< 10 min after a loss",
    revengeB: "The others",
    revengeVerdict: (gap: string) => `Expectancy gap: ${gap} per trade.`,
    twoLosses: "After 2 losses in a row",
    twoLossesQuestion: "Do you know when to stop on a bad day?",
    twoLossesA: "After 2 losses",
    twoLossesB: "Average",
    goal: "Goal reached",
    goalSubtitle: "Trades taken once the daily goal was reached",
    goalMeta: (n: number, rate: string) => `${n} trade${n > 1 ? "s" : ""} · ${rate} winners`,
    goalNotSet: "No daily PnL goal. Create one in Discipline.",
    goalCurrent: (v: string) => `Current goal: ${v} per day.`,
    byRank: "By trade rank in the day",
    byRankSubtitle: "Fatigue and overconfidence show up here",
    byMood: "By mood of the day",
    byMoodSubtitle: "Based on the mood logged in your trading sessions",
    moodEmpty: "No mood logged",
    moodEmptyText: "Log your mood at the end of a trading session: we'll cross it with your results here.",
    byTag: "By tag",
    byTagSubtitle: "The tags you put on your trades",
    tagEmpty: "No tags on these trades",
    tagEmptyText: "Add tags from a trade's detail to see what they earn or cost you.",
  },
};
